import { useEffect, useState } from "react";
import {
  DeltaViewType,
  DimensionOutroSummary,
} from "@dimensional-engineering/dimensional-models";
import { ArchetypeDiscovery as ArchetypeDiscoveryModel } from "@dimensional-engineering/dimensional-models";

import DiscoveredElements from "./discoveredElements/DiscoveredElements";
import {
  NatureDiscoveryDeltaView,
  NatureDiscoveryStepOne,
} from "./NatureDiscovery/NatureDiscovery";
import SignatureDiscoveredElements from "./signatureDiscoveredElements/SignatureDiscoveredElements";
import { useModalContext } from "@/context/ModalContext";
import AppReviewFlowModal from "@/components/shared/AppReviewFlowModal/AppReviewFlowModal";
import DimensionSummariesOutro from "@/components/results/DimensionSummaries/DimensionSummariesOutro";
import { createStepsForOutro } from "../../assessmentUtils";
import {
  ArchetypeDiscoveryScreenDeltaView,
  ArchetypeDiscoveryScreenStepOne,
  ArchetypeDiscoveryScreenStepTwo,
  ArchetypeDiscoveryStepThree,
} from "./ArchetypeDiscovery/ArchetypeDiscoveryScreen";
import TouchThroughWrapper from "./TouchThroughWrapper/TouchThroughWrapper";
import TopTraitsDeltaView from "./TopTraitsDeltaView/TopTraitsDeltaView";
import SpectrumsDeltaView from "./SpectrumsDeltaView/SpectrumsDeltaView";
import TwoByTwoDeltaView from "./TwoByTwoDeltaView/TwoByTwoDeltaView";
import SingleTraitDeltaView from "./SingleTraitDeltaView/SingleTraitDeltaView";
import DeltaBreakdown from "./DeltaBreakdown/DeltaBreakdown";
import ModalWrapper from "@/components/shared/ModalWrapper/ModalWrapper";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

type Props = {
  outro: DimensionOutroSummary | null;
  slug: string;
  onFinish: () => void;
  onBoarding?: boolean;
  modal?: boolean;
};

export default function AsessmentOutroScreen(props: Props) {
  const { setModalComponent } = useModalContext();

  const [currentArchetype, setCurrentArchetype] =
    useState<ArchetypeDiscoveryModel | null>(null);
  const [step, setStep] = useState<number>(0);
  const [stepMap, setStepMap] = useState<Record<number, string>>({});

  const archetypeDiscoveries = useSelector(
    (state: RootState) => state.user.archetypesDiscovered
  );

  useEffect(() => {
    setStepMap(createStepsForOutro(props.outro, archetypeDiscoveries));
  }, [props.outro, archetypeDiscoveries]);

  useEffect(() => {
    if (props.outro?.archetypeDiscoveries) {
      setCurrentArchetype(props.outro.archetypeDiscoveries[0]);
    }
  }, [props.outro?.archetypeDiscoveries]);

  function onArchetypeFinished(arch: ArchetypeDiscoveryModel | null) {
    //going to next archetype
    const filtered = props.outro?.archetypeDiscoveries.filter(
      (a) => a.archetypeSlug != arch?.archetypeSlug
    );
    if (filtered?.length) {
      console.log(filtered[0]);
      setCurrentArchetype(filtered[0]);
    }
  }

  const needsBreakdownOutro =
    props.outro?.dimensionSummary?.deltaViews?.find(
      (d) => d.deltaViewType === DeltaViewType.topTraitDelta
    ) != null ||
    props.outro?.dimensionSummary?.deltaViews?.find(
      (d) => d.deltaViewType === DeltaViewType.twoByTwoDelta
    );

  const outroStepMap: Record<string, JSX.Element> = {
    NatureDiscoveryStepOne: (
      <NatureDiscoveryStepOne
        onBoarding={props.onBoarding}
        primaryNature={props.outro?.primaryNature}
      />
    ),
    NatureDiscoveryDeltaView: (
      <NatureDiscoveryDeltaView
        natureDeltaData={
          props.outro?.dimensionSummary?.deltaViews?.find(
            (d) => d.deltaViewType === DeltaViewType.natureDelta
          )?.deltaViewData?.natureDeltaData
        }
      />
    ),
    ArchetypeDiscoveryScreenStepOne: (
      <ArchetypeDiscoveryScreenStepOne archetype={currentArchetype} />
    ),
    ArchetypeDiscoveryScreenStepTwo: (
      <ArchetypeDiscoveryScreenStepTwo archetype={currentArchetype} />
    ),
    ArchetypeDiscoveryStepThree: (
      <ArchetypeDiscoveryStepThree archetype={currentArchetype} />
    ),
    ArchetypeDiscoveryScreenDeltaView: (
      <ArchetypeDiscoveryScreenDeltaView
        archetypeDeltaData={
          props.outro?.dimensionSummary?.deltaViews?.find(
            (d) => d.deltaViewType === DeltaViewType.archetypeDelta
          )?.deltaViewData?.archetypeDeltaData
        }
      />
    ),
    TopTraitsDeltaView: (
      <TopTraitsDeltaView
        topNDeltaData={
          props.outro?.dimensionSummary?.deltaViews?.find(
            (d) => d.deltaViewType === DeltaViewType.topNDelta
          )?.deltaViewData?.topNDeltaData
        }
        dimensionSlug={props.slug}
      />
    ),
    SpectrumsDeltaView: <SpectrumsDeltaView slug={props.slug} />,
    TwoByTwoDeltaView: (
      <TwoByTwoDeltaView
        onFinish={() => {
          setStep((current) => current + 1);
        }}
        slug={props.slug}
      />
    ),
    SingleTraitDeltaView: (
      <SingleTraitDeltaView
        topTraitDeltaData={
          props.outro?.dimensionSummary?.deltaViews?.find(
            (d) => d.deltaViewType === DeltaViewType.topTraitDelta
          )?.deltaViewData?.topTraitDeltaData
        }
        onFinish={() => {
          setStep((current) => current + 1);
        }}
      />
    ),
    DiscoveredElements: <DiscoveredElements outro={props.outro ?? undefined} />,
    SignatureDiscoveredElements: (
      <SignatureDiscoveredElements
        onFinish={() => {
          if (props.slug === "values") {
            setModalComponent(<AppReviewFlowModal onFinish={() => {}} />);
          }
          setStep((current) => current + 1);
        }}
        outro={props.outro ?? undefined}
      />
    ),
  };

  const current = stepMap[step];

  if (step > Object.keys(stepMap).length - 1) {
    if (needsBreakdownOutro) {
      if (props.modal) {
        return (
          <ModalWrapper title="" noHeader noLine>
            <DeltaBreakdown
              onFinish={() => props.onFinish()}
              onBack={() => setStep((current) => current - 1)}
              outro={props.outro}
              slug={props.slug}
              source="latest results item"
            />
          </ModalWrapper>
        );
      }
      return (
        <DeltaBreakdown
          onFinish={() => props.onFinish()}
          onBack={() => setStep((current) => current - 1)}
          outro={props.outro}
          slug={props.slug}
          source="assessment completion"
        />
      );
    }
    return (
      <DimensionSummariesOutro
        slug={props.slug}
        outro={props.outro}
        onContinue={() => props.onFinish()}
      />
    );
  }

  if (props.modal) {
    return (
      <ModalWrapper title="" noHeader noLine>
        <TouchThroughWrapper
          dimensionSlug={props.slug}
          numberOfSteps={Object.keys(stepMap).length}
          currentStep={step}
          onNext={() => {
            try {
              if (window.Android?.onHaptic) {
                window.Android.onHaptic();
              }
            } catch (error) {}

            if (current === "ArchetypeDiscoveryStepThree") {
              onArchetypeFinished(currentArchetype);
            }
            setStep((current) => current + 1);
          }}
          onPrevious={() => {
            if (step === 0) {
              return;
            }
            try {
              if (window.Android?.onHaptic) {
                window.Android.onHaptic();
              }
            } catch (error) {}
            setStep((current) => current - 1);
          }}
          inModal
        >
          {outroStepMap[current]}
        </TouchThroughWrapper>
      </ModalWrapper>
    );
  }

  return (
    <TouchThroughWrapper
      dimensionSlug={props.slug}
      numberOfSteps={Object.keys(stepMap).length}
      currentStep={step}
      onNext={() => {
        try {
          if (window.Android?.onHaptic) {
            window.Android.onHaptic();
          }
        } catch (error) {}

        if (current === "ArchetypeDiscoveryStepThree") {
          onArchetypeFinished(currentArchetype);
        }
        setStep((current) => current + 1);
      }}
      onPrevious={() => {
        if (step === 0) {
          return;
        }
        try {
          if (window.Android?.onHaptic) {
            window.Android.onHaptic();
          }
        } catch (error) {}
        setStep((current) => current - 1);
      }}
    >
      {outroStepMap[current]}
    </TouchThroughWrapper>
  );
}
